import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  // Step: 'credentials' | 'whatsapp' | 'setup_totp' | 'verify_otp' | 'recover'
  const [step, setStep] = useState('credentials');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [whatsapp, setWhatsapp] = useState('');
  const [otp, setOtp] = useState('');
  const [pendingId, setPendingId] = useState('');
  const [totpInfo, setTotpInfo] = useState(null); // {totp_uri, totp_secret, qr_data_uri}
  const [confirmMode, setConfirmMode] = useState(false); // si venimos de setup_totp
  const [recoverPwd, setRecoverPwd] = useState('');
  const [recoverSuccess, setRecoverSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Si ya hay sesión Google con role='admin' → entrar directo (sin TOTP).
  // O si hay sesión TOTP vigente, también.
  useEffect(() => {
    // Esperamos a que AuthContext termine de cargar antes de decidir.
    // Sin esto, hay un race condition donde user=null momentáneamente y
    // se muestra el formulario aunque el usuario SÍ tenga sesión admin.
    if (authLoading) return;
    if (user?.role === 'admin') {
      localStorage.setItem('bebida-bacana-admin', 'true');
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    const token = localStorage.getItem('bebida-bacana-admin-token');
    if (!token) return;
    axios.post(`${API}/admin/session/check`, { token })
      .then(() => navigate('/admin/dashboard', { replace: true }))
      .catch(() => {
        localStorage.removeItem('bebida-bacana-admin-token');
        localStorage.removeItem('bebida-bacana-admin');
      });
  }, [navigate, user, authLoading]);

  const persistSession = (data) => {
    localStorage.setItem('bebida-bacana-admin', 'true');
    if (data?.token) localStorage.setItem('bebida-bacana-admin-token', data.token);
    if (data?.expires_at) localStorage.setItem('bebida-bacana-admin-expires', data.expires_at);
  };

  const goToStep = (next, data = {}) => {
    if (next === 'setup_totp') {
      setTotpInfo({ totp_uri: data.totp_uri, totp_secret: data.totp_secret, qr_data_uri: data.qr_data_uri });
      setPendingId(data.pending_id);
      setConfirmMode(true);
      setStep('setup_totp');
    } else if (next === 'verify_otp') {
      setPendingId(data.pending_id);
      setConfirmMode(false);
      setStep('verify_otp');
    }
  };

  const callStart = async (body) => {
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login/start`, body);
      goToStep(r.data.next_step, r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError(detail || 'Demasiados intentos. Esperá unos minutos.');
      } else {
        setError(detail || 'Credenciales inválidas.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialsSubmit = (e) => {
    e.preventDefault();
    callStart({ username: credentials.username, password: credentials.password });
  };

  const handleWhatsappSubmit = (e) => {
    e.preventDefault();
    callStart({ whatsapp });
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const endpoint = confirmMode ? 'confirm-totp' : 'verify-otp';
      const r = await axios.post(`${API}/admin/login/${endpoint}`, { pending_id: pendingId, code: otp });
      persistSession(r.data);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError(detail || 'Demasiados intentos. Esperá unos minutos.');
      } else if (err.response?.status === 400) {
        setError(detail || 'Sesión expirada. Iniciá de nuevo.');
        backToLogin();
      } else {
        setError(detail || 'Código incorrecto.');
      }
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setStep(whatsapp ? 'whatsapp' : 'credentials');
    setOtp(''); setPendingId(''); setTotpInfo(null);
    setError('');
  };

  const handleGoogleAdminLogin = async () => {
    setError('');
    setGoogleLoading(true);
    // Fast-path: si el user ya viene con role='admin' desde su sesión
    // Google (por SUPER_ADMIN_EMAILS o promoción manual), entramos
    // DIRECTO al dashboard sin pasar por TOTP. Este es el comportamiento
    // pactado con el usuario: "si es admin Google, no le pidas código".
    if (user?.role === 'admin') {
      localStorage.setItem('bebida-bacana-admin', 'true');
      navigate('/admin/dashboard', { replace: true });
      setGoogleLoading(false);
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/verify-role`, {}, { withCredentials: true });
      if (res.data?.next_step) {
        goToStep(res.data.next_step, res.data);
      }
    } catch (err) {
      if (err.response?.status === 403) setError('Tu cuenta no tiene permisos de administrador.');
      else if (err.response?.status === 401) setError('Primero iniciá sesión con Google en la tienda.');
      else if (err.response?.status === 429) setError(err.response.data?.detail || 'Demasiados intentos.');
      else setError('Error al verificar permisos.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleRecover = async (e) => {
    e?.preventDefault();
    setError(''); setLoading(true);
    try {
      const body = whatsapp ? { whatsapp } : { username: credentials.username || 'mauricio', password: recoverPwd };
      await axios.post(`${API}/admin/security/recover`, body);
      setRecoverSuccess(true);
      setTimeout(() => {
        setStep(whatsapp ? 'whatsapp' : 'credentials');
        setOtp(''); setPendingId(''); setRecoverPwd(''); setRecoverSuccess(false);
      }, 1800);
    } catch (err) {
      setError(err.response?.data?.detail || 'Credenciales incorrectas.');
    } finally {
      setLoading(false);
    }
  };

  /* ─────── RENDERS ─────── */
  if (step === 'recover') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="Bebida Bacana" className="h-32 w-auto mx-auto mb-3" />
            <h1 className="text-xl font-semibold text-slate-900">Resetear autenticador</h1>
            <p className="text-sm text-slate-500 mt-1">
              Confirmá tus credenciales master. Tras el reset, el próximo login mostrará un nuevo QR para escanear.
            </p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
            {recoverSuccess ? (
              <div className="text-center py-4">
                <div className="text-emerald-600 text-3xl mb-2">✓</div>
                <p className="font-semibold text-slate-800">TOTP reseteado.</p>
                <p className="text-sm text-slate-500 mt-1">Volviendo al login...</p>
              </div>
            ) : (
              <form onSubmit={handleRecover} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Contraseña master <span className="text-slate-400 font-normal">(si entrás por usuario/email)</span>
                  </label>
                  <input
                    type="password"
                    value={recoverPwd}
                    onChange={(e) => setRecoverPwd(e.target.value)}
                    placeholder="Tu contraseña de admin"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    data-testid="admin-recover-password-input"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Si iniciaste por WhatsApp, el reset se hace con tu número (ya validado).
                  </p>
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="admin-login-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 rounded-lg active:scale-95 transition-transform disabled:opacity-50"
                  data-testid="admin-recover-submit"
                >
                  {loading ? 'Reseteando...' : 'Resetear autenticador'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep(whatsapp ? 'whatsapp' : 'credentials'); setError(''); setRecoverPwd(''); }}
                  className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
                  data-testid="admin-recover-back"
                >
                  ← Volver al login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'setup_totp' || step === 'verify_otp') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="Bebida Bacana" className="h-32 w-auto mx-auto mb-3" />
            <h1 className="text-xl font-semibold text-slate-900">
              {confirmMode ? 'Activá tu Autenticador' : 'Código de verificación'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {confirmMode
                ? 'Escaneá el QR con Google Authenticator (o Authy / Microsoft Authenticator) y luego pegá el código de 6 dígitos.'
                : 'Abrí tu app autenticadora y pegá el código de 6 dígitos.'}
            </p>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
            {confirmMode && totpInfo?.qr_data_uri && (
              <div className="mb-5">
                <img
                  src={totpInfo.qr_data_uri}
                  alt="QR TOTP"
                  className="w-56 h-56 mx-auto rounded-lg border border-slate-200"
                  data-testid="admin-totp-qr"
                />
                <div className="mt-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">¿No podés escanear? Ingresá la clave manualmente:</p>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded select-all" data-testid="admin-totp-secret">
                    {totpInfo.totp_secret}
                  </code>
                </div>
              </div>
            )}

            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Código (6 dígitos)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  placeholder="123456"
                  className="w-full text-center text-2xl tracking-[0.5em] px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none font-mono"
                  data-testid="admin-otp-input"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="admin-login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-lg active:scale-95 transition-transform disabled:opacity-50"
                data-testid="admin-otp-submit"
              >
                {loading ? 'Verificando...' : confirmMode ? 'Activar y entrar' : 'Verificar'}
              </button>

              <button
                type="button"
                onClick={backToLogin}
                className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
                data-testid="admin-otp-back"
              >
                ← Volver al inicio de sesión
              </button>

              {!confirmMode && (
                <button
                  type="button"
                  onClick={() => { setStep('recover'); setError(''); }}
                  className="w-full text-amber-700 hover:text-amber-900 text-xs font-semibold py-1"
                  data-testid="admin-otp-recover-link"
                >
                  ¿No tenés el autenticador? Resetear
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Spinner mientras AuthContext termina de resolver la sesión.
  // Evita el "parpadeo" donde por 1-2 segundos se muestra el formulario
  // legacy antes de redirigir al dashboard cuando el usuario ya es admin.
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <img src="/logo.png" alt="Bebida Bacana" className="h-24 w-auto mx-auto mb-4 opacity-80" />
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Login step (credentials | whatsapp)
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Bebida Bacana"
            className="h-40 w-auto mx-auto mb-4"
            data-testid="admin-login-logo"
          />
          <p className="text-slate-600">Panel de Administración</p>
          <p className="text-xs text-slate-400 mt-1">
            {user?.role === 'admin'
              ? 'Sesión Google detectada — acceso directo.'
              : user ? 'Verificá tus permisos de administrador.' : 'Ingresá con tu cuenta de Google.'}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
          {/* Botón Google — SIEMPRE visible (con o sin sesión previa).
              Si NO hay sesión: hace login OAuth con next=/admin/dashboard.
              Si HAY sesión: verifica rol admin y entra directo. */}
          <div className="mb-6 pb-6 border-b border-slate-200">
            {user ? (
              <>
                <button
                  onClick={handleGoogleAdminLogin}
                  disabled={googleLoading}
                  className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-3 rounded-lg active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="google-admin-login-button"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  {googleLoading ? 'Verificando...' : (user.role === 'admin' ? `Entrar como ${user.name || user.email}` : `Ingresar como ${user.name || user.email}`)}
                </button>
                <p className="text-xs text-center text-slate-400 mt-2">
                  {user.role === 'admin'
                    ? 'Acceso directo — no necesitás código verificador.'
                    : 'Después te vamos a pedir el código de tu autenticador'}
                </p>
              </>
            ) : (
              <>
                <a
                  href={`${API}/auth/google/start?next=${encodeURIComponent('/admin/dashboard')}`}
                  className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-3 rounded-lg active:scale-95 transition-transform flex items-center justify-center gap-2"
                  data-testid="google-admin-login-button"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Ingresar con Google
                </a>
                <p className="text-xs text-center text-slate-400 mt-2">
                  Si tu email es admin, entrás directo — sin contraseña ni código.
                </p>
              </>
            )}
          </div>

          {/* Formulario legacy — colapsable, para casos edge. */}
          <details className="group">
            <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 mb-4 flex items-center gap-1" data-testid="admin-legacy-toggle">
              <span className="group-open:hidden">▸ Otras formas de ingresar (usuario/WhatsApp)</span>
              <span className="hidden group-open:inline">▾ Ocultar</span>
            </summary>

            <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-lg">
              <button
                type="button"
                onClick={() => { setStep('credentials'); setError(''); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${step === 'credentials' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                data-testid="admin-tab-credentials"
              >
                Usuario / Email
              </button>
              <button
                type="button"
                onClick={() => { setStep('whatsapp'); setError(''); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${step === 'whatsapp' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                data-testid="admin-tab-whatsapp"
              >
                WhatsApp
              </button>
            </div>

          {step === 'credentials' && (
            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="username">Usuario o Email</label>
                <input
                  type="text" id="username" name="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required placeholder="mauricio o tu@email.com"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="password">
                  Contraseña <span className="text-slate-400 font-normal">(opcional con email admin)</span>
                </label>
                <input
                  type="password" id="password" name="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="Contraseña"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  data-testid="admin-password-input"
                />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="admin-login-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-lg active:scale-95 transition-transform disabled:opacity-50"
                data-testid="admin-login-button"
              >
                {loading ? 'Verificando...' : 'Continuar'}
              </button>
            </form>
          )}

          {step === 'whatsapp' && (
            <form onSubmit={handleWhatsappSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="whatsapp">Tu número de WhatsApp</label>
                <input
                  type="tel" id="whatsapp" name="whatsapp"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  required placeholder="+54 9 3571 32-0032"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  data-testid="admin-whatsapp-input"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Cualquier formato. Después te pediremos el código de tu autenticador.
                </p>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="admin-login-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-lg active:scale-95 transition-transform disabled:opacity-50"
                data-testid="admin-whatsapp-login-button"
              >
                {loading ? 'Verificando...' : 'Continuar'}
              </button>
            </form>
          )}
          </details>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
            data-testid="back-to-home-button"
          >
            ← Volver al inicio
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
